// Blocking screen shown on first login (must_change_password) — forces a new secure password
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';

export function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.current || !form.next) { toast.error('Please fill in all fields'); return; }
    if (form.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (form.next !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.next === form.current) { toast.error('Choose a password different from the temporary one'); return; }
    setSaving(true);
    try {
      await portalApi.changePassword(form.current, form.next);
      toast.success('Password updated — welcome!');
      setUser({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(err.message || 'Could not update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
        <div className="h-12 w-12 bg-sky-100 rounded-xl flex items-center justify-center mb-5">
          <ShieldCheck className="h-6 w-6 text-sky-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Set a new password</h1>
        <p className="text-slate-500 text-sm mb-6">
          Welcome, {user?.name}. For your security, please replace your temporary password before continuing.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-700">Temporary password</Label>
            <Input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
              placeholder="From your welcome email" data-testid="fpc-current" autoComplete="current-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">New password</Label>
            <Input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
              placeholder="At least 8 characters" data-testid="fpc-next" autoComplete="new-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">Confirm new password</Label>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              placeholder="Re-enter new password" data-testid="fpc-confirm" autoComplete="new-password" />
          </div>
          <Button type="submit" className="w-full" disabled={saving} data-testid="fpc-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" /> Update password</>}
          </Button>
        </form>
        <button onClick={logout} className="mt-4 text-sm text-slate-400 hover:text-slate-600 w-full text-center" data-testid="fpc-logout">
          Sign out
        </button>
      </div>
    </div>
  );
}

export default ForcePasswordChange;
